import { useState, useEffect } from 'react';
import { translations, TranslatedContent } from '../translations';

const LANGUAGE_KEY = 'nexucapital_preferred_language';

export const useTranslation = (initialLanguage?: string) => {
  // Get saved language from localStorage or use browser language as fallback
  const getSavedLanguage = (): string => {
    if (typeof window !== 'undefined') {
      // First try to get from localStorage
      const saved = localStorage.getItem(LANGUAGE_KEY);
      if (saved && translations[saved]) {
        return saved;
      }

      // Then try browser language
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith('en')) return 'en';
      if (browserLang.startsWith('es')) return 'es';
      if (browserLang.startsWith('pt')) return 'pt-br';
    }

    return 'pt-br'; // Default fallback
  };

  const [currentLanguage, setCurrentLanguage] = useState<string>(
    initialLanguage || getSavedLanguage()
  );

  // Save language preference to localStorage
  const saveLanguagePreference = (language: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_KEY, language);
    }
  };

  // Update language and save preference
  const changeLanguage = (language: string) => {
    if (translations[language]) {
      setCurrentLanguage(language);
      saveLanguagePreference(language);
    }
  };

  // Get translations for current language
  const getTranslations = (): TranslatedContent => {
    return translations[currentLanguage] || translations['pt-br'];
  };

  // Effect to sync with initialLanguage changes
  useEffect(() => {
    if (initialLanguage && initialLanguage !== currentLanguage) {
      changeLanguage(initialLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLanguage]);

  return {
    currentLanguage,
    changeLanguage,
    translations: getTranslations(),
    availableLanguages: Object.keys(translations)
  };
};

// Legacy function for backward compatibility
export const useTranslationLegacy = (currentLanguage: string): TranslatedContent => {
  return translations[currentLanguage] || translations['pt-br'];
};

export default useTranslation;